<template>
  <div class="failed-products-table-wrapper">
    <div class="table-wrapper">
      <table class="advanced-table">
        <thead>
          <tr>
            <th style="width: 250px">Ürün Adı</th>
            <th style="width: 160px">Stok Kodu</th>
            <th style="width: 120px">İşlem Sonucu</th>
            <th>Açıklama</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in paginatedData" :key="item.productId">
            <td class="product-name-cell" :title="item.title">
              {{ item.title }}
            </td>
            <td>{{ item.skuId }}</td>
            <td>
              <span
                :class="['status-badge', isWarning(item) ? 'status-warning' : 'status-fail']"
              >
                <img
                  width="12px"
                  height="12px"
                  :src="isWarning(item) ? infoCircleIcon : alertCircleIcon"
                  :alt="isWarning(item) ? 'Warning Icon' : 'Error Icon'"
                />
                {{ isWarning(item) ? 'Uyarı' : 'Hata' }}
              </span>
            </td>
            <td class="reason-text">{{ item.reason }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="pageCount > 1" class="pagination">
      <Pagination
        :pageCount="pageCount"
        :clickHandler="handlePageChange"
        :value="currentPage"
        :pageRange="5"
        firstLastButton="true"
        containerClass="button-group"
        prevLinkClass="border left rounded p-1"
        pageLinkClass="border rounded p-1"
        nextLinkClass="border right rounded p-1"
        :marginPages="0"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import Pagination from '@/components/Table/Pagination';
import alertCircleIcon from '@/assets/images/icons/alert-circle.svg';
import infoCircleIcon from '@/assets/images/icons/info-circle.svg';

const PAGE_SIZE = 6;

const props = defineProps({
  failedProducts: {
    type: Array,
    required: true,
    default: () => [],
  },
});

const currentPage = ref(0);

watch(() => props.failedProducts, () => {
  currentPage.value = 0;
});

const pageCount = computed(() => {
  return Math.ceil(props.failedProducts.length / PAGE_SIZE);
});

const paginatedData = computed(() => {
  const start = currentPage.value * PAGE_SIZE;
  const end = start + PAGE_SIZE;
  return props.failedProducts.slice(start, end);
});

const isWarning = (item) => item.level?.levelStr === 'WARN';

const handlePageChange = (pageNum) => {
  currentPage.value = pageNum;
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';

.failed-products-table-wrapper {
  display: flex;
  gap: 16px;
  flex-direction: column;

  .table-wrapper {
    width: 100%;
    position: relative;
    border: 1px solid $colorD20;
    border-radius: 8px;
    border-bottom: 0px;
    border-spacing: 0;
    border-collapse: separate;
    overflow-y: auto;
    scroll-behavior: smooth;
    max-height: 380px;
  }

  .advanced-table {
    width: 100%;
    border-radius: 8px;
    border-collapse: separate;

    thead {
      tr {
        th {
          padding: 16px 12px;
          text-align: left;
          font-weight: 600;
          color: $colorN80;
          font-size: 13px;
          line-height: 18px;
          background: $colorBe30;
        }
        th:not(:last-child) {
          border-right: 1px solid $colorD20;
        }
      }
    }

    tbody {
      tr {
        td {
          font-weight: 600;
          padding: 11px 12px;
          text-align: left;
          border-bottom: 2px solid $colorD10;
          font-size: 13px;
          letter-spacing: -0.05px;
          line-height: 18px;
          vertical-align: middle;
        }
        td:not(:last-child) {
          border-right: 1px solid $colorD20;
        }
      }
    }
  }

  .product-name-cell {
    max-width: 250px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: $colorN80;
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 4px;
    border: 0.5px solid $colorN30;
    font-size: 11px;
    font-weight: 600;
    line-height: 14px;
    white-space: nowrap;

    &.status-fail {
      background: $colorE05;
      color: $colorE60;
    }

    &.status-warning {
      background: $colorB05;
      color: $colorB60;
    }
  }

  .reason-text {
    font-size: 13px;
    line-height: 18px;
    color: $colorN80;
    font-weight: 600;
  }

  .pagination {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
